import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Shield, Wrench } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [portalDropdownOpen, setPortalDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPortalDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Find an Engineer', href: '/find-engineer' },
    { name: 'For Engineers', href: '/for-engineers' },
    { name: 'Our Standards', href: '/standards' },
    { name: 'About', href: '/about' },
    { name: 'Partners', href: '/partners' },
    { name: 'Contact', href: '/contact' }
  ];

  const portalLinks = [
    { name: 'Engineer Portal', href: '/engineer/login', icon: Wrench, description: 'Manage your listing' },
    { name: 'Admin Portal', href: '/admin/login', icon: Shield, description: 'Admin access only' }
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header className="header-main">
      <div className="header-container">
        <div className="header-content">
          {/* Logo */}
          <Link to="/" className="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_trustygas/artifacts/191wqlf3_file_000000004ca871f48b719ce9d2a32667.png" 
              alt="The Gas Guide UK"
              className="logo-image"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'nav-link-active' : ''}`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Portal Dropdown */}
            <div className="portal-dropdown" ref={dropdownRef}>
              <button
                className="nav-link portal-trigger"
                onClick={() => setPortalDropdownOpen(!portalDropdownOpen)}
              >
                Portals <ChevronDown size={16} className={`dropdown-chevron ${portalDropdownOpen ? 'rotate' : ''}`} />
              </button>
              {portalDropdownOpen && (
                <div className="portal-dropdown-menu">
                  {portalLinks.map((portal) => (
                    <Link
                      key={portal.name}
                      to={portal.href}
                      className="portal-dropdown-item"
                      onClick={() => setPortalDropdownOpen(false)}
                    >
                      <portal.icon size={18} />
                      <div>
                        <span className="portal-name">{portal.name}</span>
                        <span className="portal-description">{portal.description}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            className="mobile-menu-button"
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              // Scroll to top when opening menu
              if (!mobileMenuOpen) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`mobile-nav-link ${isActive(item.href) ? 'mobile-nav-link-active' : ''}`}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                {item.name}
              </Link>
            ))}
            <div className="mobile-portal-divider"></div>
            {portalLinks.map((portal) => (
              <Link
                key={portal.name}
                to={portal.href}
                className="mobile-nav-link mobile-portal-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <portal.icon size={16} />
                {portal.name}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
